import React, { useState } from 'react';
import { Pressable } from 'react-native';

import { ProjectTag } from '@components/Chat/ProjectTag';
import TaskTag, { TaskTagProps } from '@components/Chat/TaskTag';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import { SkeletonList } from '@components/shared/skeletons/SkeletonList';
import { TagRowSkeleton } from '@components/shared/skeletons/TagRowSkeleton';
import { TaskSortOption } from '@graphql/generated';
import { useListTaskTagsFromQuery } from '@hooks/useListTaskTagsFromQuery';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { ProjectTagType } from '@src/types/project';
import { TaskTagType } from '@src/types/task';

interface ProjectTaskSectionProps {
  project: ProjectTagType;
  filterVal?: string;
  onSelectProject: (project: ProjectTagType) => void;
  onSelectTask: (task: TaskTagType) => void;
  /** When provided, renders these tasks directly instead of fetching. */
  staticTasks?: TaskTagType[];
  /**
   * TA-10502: when staticTasks came from a batched first-page fetch (parent
   * batches listTaskTags across all visible projects), "Load more" falls back
   * to this row's own paginated query on first press instead of never
   * appearing. Not set for search-mode staticTasks, which are already the
   * complete result set.
   */
  canLoadMoreFromStatic?: boolean;
  /**
   * TA-10502 (QA fix): whether the batch may have more tasks for this project
   * than it returned. The combined server page is shared across every batched
   * project, so a starved project can come back with 0-9 tasks even though it
   * has more; without this the row treated any short group as complete and
   * never exposed "Load more tasks", making those tasks unreachable. Defaults
   * to the old length heuristic when the parent does not report it.
   */
  staticTasksMayHaveMore?: boolean;
  /** TA-10502: surfaces the parent batch query's own loading state. */
  isStaticTasksLoading?: boolean;
  excludeCompleted?: boolean;
  /** TA-10279: opt-in so native (and any other caller) keeps the plain spinner. */
  showLoadingSkeleton?: boolean;
}

const BATCH_PAGE_SIZE = 10;

export const ProjectTaskSection: React.FC<ProjectTaskSectionProps> = ({
  project,
  filterVal,
  onSelectProject,
  onSelectTask,
  staticTasks,
  canLoadMoreFromStatic = false,
  staticTasksMayHaveMore,
  isStaticTasksLoading = false,
  excludeCompleted,
  showLoadingSkeleton = false,
}) => {
  // Once the row's own paginated query has been activated (either because no
  // staticTasks were supplied, or the row explicitly requested more than the
  // batched first page held), it takes over as the source of truth.
  //
  // The activation is scoped to `canLoadMoreFromStatic` on purpose: TagModal
  // keys its FlatList rows by project.id, so the same instance survives a
  // browse -> search transition. Search-mode staticTasks are the complete
  // result set (canLoadMoreFromStatic=false) and must win over an activation
  // that happened while browsing, otherwise the row would keep showing the
  // browse task list while the user is searching.
  const [ownQueryActivated, setOwnQueryActivated] = useState(false);
  const useOwnQuery =
    staticTasks === undefined || (ownQueryActivated && canLoadMoreFromStatic);

  // TA-10502 (QA fix): the batched page lives under a different cache key
  // (keyArgs include projectIds), so activating the row's own query refetches
  // this project's first page from scratch. With `first: BATCH_PAGE_SIZE` that
  // page is the same 10 tasks the row already shows, so the first "Load more"
  // press appeared to do nothing and the user had to press twice to paginate.
  // Ask for two pages on activation so one press always yields new tasks.
  const ownQueryFirst =
    ownQueryActivated && staticTasks !== undefined
      ? BATCH_PAGE_SIZE * 2
      : BATCH_PAGE_SIZE;

  const {
    tasks: fetchedTasks,
    loading: ownLoading,
    isFetchingMore,
    pageInfo: ownPageInfo,
    fetchMoreFromCursor,
  } = useListTaskTagsFromQuery({
    projectId: project.id,
    first: ownQueryFirst,
    sortBy: TaskSortOption.NameAsc,
    skip: !useOwnQuery,
    excludeCompleted,
  });

  // On the first render after "Load more" activates the row's own query, that
  // query has no data yet — swapping straight to it would blank the already
  // rendered batched page out to a skeleton and back. Keep showing the batched
  // tasks until the own query has its own first page.
  const isOwnQueryLoadingFirstPage =
    useOwnQuery && ownLoading && fetchedTasks.length === 0;
  const isShowingStaticFallback =
    isOwnQueryLoadingFirstPage && staticTasks !== undefined;
  const isReadingOwnQuery = useOwnQuery && !isShowingStaticFallback;

  const tasks = isReadingOwnQuery ? fetchedTasks : staticTasks!;
  const loading = isReadingOwnQuery ? ownLoading : isStaticTasksLoading;

  // The batched query only fetched a first page per project, so there is no
  // real per-project pageInfo to read — the parent reports whether the batch
  // may have withheld tasks for this project (a full page, or a group trimmed
  // down to the per-project cap). Pressing "Load more" activates this row's
  // own query, which supplies real pageInfo/fetchMoreFromCursor for any
  // further pages.
  const hasNextPage = isReadingOwnQuery
    ? !!ownPageInfo?.hasNextPage
    : canLoadMoreFromStatic &&
      (staticTasksMayHaveMore ?? tasks.length >= BATCH_PAGE_SIZE);
  const onLoadMore = isReadingOwnQuery
    ? fetchMoreFromCursor
    : () => setOwnQueryActivated(true);
  const isLoadingMore = isFetchingMore || isShowingStaticFallback;

  const showTaskSkeleton = useMinimumVisible(
    showLoadingSkeleton && loading && tasks.length === 0
  );
  const showSpinner = !showTaskSkeleton && loading && tasks.length === 0;

  return (
    <Box paddingHorizontal='m'>
      <Pressable
        onPress={() => onSelectProject(project)}
        accessibilityLabel={project.name}>
        <Box paddingTop='m'>
          <ProjectTag project={project} filterVal={filterVal} />
        </Box>
      </Pressable>
      <Box height={1} backgroundColor='grey01' marginTop='m' marginBottom='s' />
      {showTaskSkeleton && (
        <SkeletonList count={3} row={() => <TagRowSkeleton showAvatars />} />
      )}
      {showSpinner && <ActivityIndicatorLoading />}
      {!showTaskSkeleton && !showSpinner && (
        <>
          {tasks.map((task, index) => (
            <Box key={task.id}>
              <TaskTag
                taskName={task.name}
                members={task.members as unknown as TaskTagProps['members']}
                filterVal={filterVal}
                onSelect={() => onSelectTask(task)}
              />
              {index < tasks.length - 1 && (
                <Box height={1} marginTop='s' marginBottom='s' />
              )}
            </Box>
          ))}
          {hasNextPage && (
            <Pressable onPress={onLoadMore} disabled={loading || isLoadingMore}>
              <Box paddingTop='m' paddingBottom='s' paddingLeft='m'>
                <Text color='greenSecondary' variant='bodySecondary'>
                  {isLoadingMore ? 'Loading...' : 'Load more tasks'}
                </Text>
              </Box>
            </Pressable>
          )}
        </>
      )}
    </Box>
  );
};
